/**
 * Гидроизоляция комнаты на 2D-чертеже — компонент раздела каталога.
 *
 * Заливка цветом своего вида из каталога; наплавляемая дополнительно штрихуется
 * пчелиными сотами, чтобы отличаться от обмазочной и рулонной с одного взгляда.
 */
import { WaterproofMaterial, getWaterproofMaterial } from "@/shared/catalog";
import type { Room } from "@/widgets/plan";

const makeWaterproofPatternId = (roomId: string, material: WaterproofMaterial) =>
	`room-waterproof-${roomId}-${material}`;

const makeClipPathId = (roomId: string) => `room-waterproof-clip-${roomId}`;

const getRoomBounds = (room: Room) =>
	room.inner.reduce(
		(acc, point) => ({
			minX: Math.min(acc.minX, point.x),
			maxX: Math.max(acc.maxX, point.x),
			minY: Math.min(acc.minY, point.y),
			maxY: Math.max(acc.maxY, point.y),
		}),
		{ minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity },
	);

/**
 * Паттерн пчелинных сот для наплавляемой гидроизоляции.
 * Рисует регулярные шестиугольники.
 */
const HoneycombPattern = ({ patternId, size = 30 }: { patternId: string; size?: number }) => {
	const width = size * 2;
	const height = (size * Math.sqrt(3)) / 2;

	// Шестиугольник: 6 точек по кругу с радиусом = size
	// oxlint-disable-next-line unicorn/consistent-function-scoping
	const angle = (i: number) => (i * Math.PI) / 3; // 60 градусов между точками
	const hexPoints = Array.from({ length: 6 })
		.map((_, i) => {
			const a = angle(i);
			return `${size + size * Math.cos(a)},${height + size * Math.sin(a)}`;
		})
		.join(" ");

	return (
		<defs>
			<pattern
				id={patternId}
				x="0"
				y="0"
				width={width}
				height={height * 2}
				patternUnits="userSpaceOnUse"
			>
				{/* Основной шестиугольник в центре */}
				<polygon points={hexPoints} fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="1.5" />
				{/* Смещённый шестиугольник справа */}
				<polygon
					points={`${width + size},${height},${width + size * 2},${height + height},${width + size * 2},${height + height * 2},${width + size},${height * 3},${width},${height * 2},${width},${height}`}
					fill="none"
					stroke="rgba(0,0,0,0.12)"
					strokeWidth="1.5"
				/>
				{/* Смещённый шестиугольник слева */}
				<polygon
					points={`${-size},${height},0,${height + height},0,${height + height * 2},${-size},${height * 3},${-size * 2},${height * 2},${-size * 2},${height}`}
					fill="none"
					stroke="rgba(0,0,0,0.12)"
					strokeWidth="1.5"
				/>
			</pattern>
		</defs>
	);
};

export const WaterproofShape = ({
	room,
	material,
}: {
	room: Room;
	material: WaterproofMaterial;
}) => {
	const baseColor = getWaterproofMaterial(material).color;
	const patternId = makeWaterproofPatternId(room.id, material);
	const clipPathId = makeClipPathId(room.id);
	const isMeltable = material === WaterproofMaterial.Meltable;
	const bounds = getRoomBounds(room);

	return (
		<g style={{ pointerEvents: "auto" }}>
			<defs>
				<clipPath id={clipPathId}>
					<polygon points={room.inner.map((p) => `${p.x},${p.y}`).join(" ")}></polygon>
				</clipPath>
				{isMeltable && <HoneycombPattern patternId={patternId} />}
			</defs>
			<polygon
				points={room.inner.map((p) => `${p.x},${p.y}`).join(" ")}
				fill={baseColor}
				stroke="rgba(0,0,0,0.04)"
				strokeWidth={1}
				style={{ pointerEvents: "auto" }}
			/>
			{isMeltable && (
				<rect
					x={bounds.minX - 500}
					y={bounds.minY - 500}
					width={bounds.maxX - bounds.minX + 1000}
					height={bounds.maxY - bounds.minY + 1000}
					fill={`url(#${patternId})`}
					clipPath={`url(#${clipPathId})`}
					style={{ pointerEvents: "none" }}
				/>
			)}
		</g>
	);
};
